import { InferType } from "yup";
import { EmploymentVerificationCriteriaSchemaV1 } from "../../pages/adjudication/types/employment-verification-criteria-schema-v1";
import { RubricRuleset } from "./types";
import { useTranslation } from "react-i18next";
import { ActionList, ActionListItem, Stack, Text } from "@certn/cascadia";
import { DataDisplay, GhostIconButton } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import {
  AdjudicationStatus,
  AdjudicationStatusTag,
} from "../../utils/renderStatusTag";
import {
  getCheckTypeName,
  getDateVarianceScore,
  getMismatchScore,
  getUnverifiedEmploymentScore,
} from "../../pages/adjudication/utils";

type Props = {
  ruleset: RubricRuleset & {
    criteria: InferType<typeof EmploymentVerificationCriteriaSchemaV1>;
  };
  selectComponent?: React.ReactNode;
  selectedRule: string | null;
  setSelectedRule: (rule: string | null) => void;
};

export function PreviewEmploymentVerificationsCheckRuleset({
  ruleset,
  selectComponent,
  selectedRule,
  setSelectedRule,
}: Props) {
  const { t } = useTranslation();

  const heading = selectedRule
    ? getRuleLabel(selectedRule, t)
    : getCheckTypeName(ruleset.check_type, t);

  return (
    <Stack gap="space-4">
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack gap="space-0">
          <Text style="global/heading-large">{heading}</Text>
          {selectedRule && (
            <Text style="form/body-secondary">
              {getCheckTypeName(ruleset.check_type, t)}
            </Text>
          )}
        </Stack>
        {selectComponent}
      </Stack>

      {selectedRule ? (
        <RuleDetailView
          ruleType={selectedRule as RuleType}
          ruleset={ruleset.criteria.ruleset}
        />
      ) : (
        <RulesList ruleset={ruleset} setSelectedRule={setSelectedRule} />
      )}
    </Stack>
  );
}

function getRuleLabel(ruleType: string, t: (key: string) => string): string {
  switch (ruleType) {
    case "mismatch":
      return t("Mismatch Count");
    case "dateVariance":
      return t("Date Variance (start/end dates)");
    case "unverifiedEmployment":
      return t("Unverified Employment Count");
    default:
      return ruleType;
  }
}

type Ruleset = InferType<
  typeof EmploymentVerificationCriteriaSchemaV1
>["ruleset"];

type RuleType = "mismatch" | "dateVariance" | "unverifiedEmployment";

function RulesList({
  ruleset,
  setSelectedRule,
}: Pick<Props, "ruleset" | "setSelectedRule">) {
  const { t } = useTranslation();

  const rules = [
    {
      key: "mismatch",
      label: t("Mismatch Count"),
      score: getMismatchScore(ruleset.criteria.ruleset),
    },
    {
      key: "dateVariance",
      label: t("Date Variance (start/end dates)"),
      score: getDateVarianceScore(ruleset.criteria.ruleset),
    },
    {
      key: "unverifiedEmployment",
      label: t("Unverified Employment Count"),
      score: getUnverifiedEmploymentScore(ruleset.criteria.ruleset),
    },
  ];

  return (
    <Stack gap="space-4">
      <ActionList>
        {rules.map((rule) => (
          <ActionListItem
            key={rule.key}
            className={css({
              borderBottom: "1px solid var(--border-base)",
              paddingBottom: "var(--space-4)",
              paddingTop: "var(--space-4)",
            })}
            testID={`rule-${rule.key}`}
            title={rule.label}
            trailing={
              <Stack orientation="horizontal" align="center" gap="space-2">
                {AdjudicationStatusTag(rule.score as AdjudicationStatus)}
                <GhostIconButton
                  icon="chevron_right"
                  onPress={() => setSelectedRule(rule.key)}
                />
              </Stack>
            }
          />
        ))}
      </ActionList>
    </Stack>
  );
}

interface RuleDetailViewProps {
  ruleType: RuleType;
  ruleset: Ruleset;
}

function RuleDetailView({ ruleType, ruleset }: RuleDetailViewProps) {
  const { t } = useTranslation();

  const getRuleData = () => {
    switch (ruleType) {
      case "mismatch":
        return [
          {
            key: "one-instance",
            condition: t("1 Mismatch"),
            rules: ruleset.mismatch_count?.one_instance || "NOT_SET",
          },
          {
            key: "two-or-more",
            condition: t("2+ Mismatches"),
            rules: ruleset.mismatch_count?.two_or_more_instances || "NOT_SET",
          },
        ];
      case "dateVariance":
        return [
          {
            key: "ninety-or-less",
            condition: t("≤ 90 Threshold days"),
            rules: ruleset.date_variance?.ninety_days_or_less || "NOT_SET",
          },
          {
            key: "over-ninety",
            condition: t("> 90 Threshold days"),
            rules: ruleset.date_variance?.over_ninety_days || "NOT_SET",
          },
        ];
      case "unverifiedEmployment":
        return [
          {
            key: "one-instance",
            condition: t("1 Unverified"),
            rules:
              ruleset.unverified_employment_count?.one_instance || "NOT_SET",
          },
          {
            key: "two-or-more",
            condition: t("2+ Unverified"),
            rules:
              ruleset.unverified_employment_count?.two_or_more_instances ||
              "NOT_SET",
          },
        ];
    }
  };

  return (
    <DataDisplay
      renderTopMargin={false}
      columns={[
        {
          key: "condition",
          name: t("Condition"),
          isRowHeader: true,
          render: ({ condition }) => (
            <Text style="input/helper">{condition}</Text>
          ),
        },
        {
          key: "rules",
          name: t("Rules"),
          render: ({ rules }) =>
            AdjudicationStatusTag(rules as AdjudicationStatus),
        },
      ]}
      rows={getRuleData()}
    />
  );
}
